<template>
  <Teleport to="body">
  <div class="modal fade payroll-setup-modal" id="allowanceTypesManagementModal" tabindex="-1" aria-labelledby="allowanceTypesManagementModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="allowanceTypesManagementModalLabel">Allowance Types Management</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading allowance types...</p>
          </div>

          <div v-else>
            <!-- Controls section -->
            <div class="d-flex justify-content-between mb-3">
              <h6 class="mb-0 mt-2">Manage Allowance Types</h6>
              <button @click="showAddModal" class="btn btn-sm btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Allowance Type
              </button>
            </div>

            <!-- Allowance types table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Allowance Name</th>
                  <th>Amount Type</th>
                  <th>Default Value</th>
                  <th>Taxable</th>
                  <th>Description</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="allowanceTypes.length === 0">
                  <td colspan="6" class="text-center py-3">No allowance types found</td>
                </tr>
                <tr v-for="allowance in allowanceTypes" :key="allowance.id">
                  <td>{{ allowance.allowance_name }}</td>
                  <td>{{ allowance.is_percentage === '1' ? 'Percentage' : 'Fixed Amount' }}</td>

                  <td>
                    {{ allowance.is_percentage === '1'
                      ? allowance.default_percentage + '%'
                      : formatCurrency(allowance.default_amount) }}
                  </td>

                  <td>
                  <span class="badge" :class="allowance.is_taxable === '1' ? 'bg-success' : 'bg-warning'">
  {{ allowance.is_taxable === '1' ? 'Yes' : 'No' }}
</span>

                  </td>
                  <td>{{ allowance.description || 'N/A' }}</td>
                  <td class="text-center">
                    <button @click="editAllowanceType(allowance)" class="btn btn-sm btn-info me-1">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button @click="deleteAllowanceType(allowance.id)" class="btn btn-sm btn-danger">
                      <i class="mdi mdi-trash-can"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>

  <!-- Add/Edit Allowance Type Modal -->
  <Teleport to="body">
  <div class="modal fade payroll-setup-modal" id="allowanceTypeFormModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Edit Allowance Type' : 'Add Allowance Type' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveAllowanceType">
            <div class="mb-3">
              <label for="allowanceName" class="form-label">Allowance Name <span class="text-danger">*</span></label>
              <input
                  type="text"
                  class="form-control"
                  id="allowanceName"
                  v-model="allowanceForm.allowance_name"
                  required
              >
            </div>

            <div class="mb-3">
              <label class="form-label">Amount Type <span class="text-danger">*</span></label>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="fixed-amount"
                    value="fixed"
                    v-model="allowanceForm.amount_type"
                >
                <label class="form-check-label" for="fixed-amount">Fixed Amount</label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="percentage"
                    value="percentage"
                    v-model="allowanceForm.amount_type"
                >
                <label class="form-check-label" for="percentage">Percentage of Basic Salary</label>
              </div>
            </div>

            <div class="mb-3" v-if="allowanceForm.amount_type === 'fixed'">
              <label for="defaultAmount" class="form-label">Default Amount</label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="defaultAmount"
                    v-model="allowanceForm.default_amount"
                    min="0"
                    step="0.01"
                >
              </div>
            </div>

            <div class="mb-3" v-if="allowanceForm.amount_type === 'percentage'">
              <label for="defaultPercentage" class="form-label">Default Percentage</label>
              <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    id="defaultPercentage"
                    v-model="allowanceForm.default_percentage"
                    min="0"
                    max="100"
                    step="0.01"
                >
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="isTaxable"
                    v-model="allowanceForm.is_taxable"
                >
                <label class="form-check-label" for="isTaxable">
                  Taxable Allowance
                </label>
              </div>
              <small class="text-muted">If checked, this allowance will be included in taxable income calculation</small>
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea
                  class="form-control"
                  id="description"
                  v-model="allowanceForm.description"
                  rows="3"
              ></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="saveAllowanceType"
              :disabled="!isFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
/* eslint-disable */
import * as bootstrap from "bootstrap";
import { authUrl, execute } from "@/api";

export default {
  name: 'AllowanceTypesManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      allowanceTypes: [],
      isEditing: false,
      currentId: null,
      allowanceForm: {
        allowance_name: '',
        amount_type: 'fixed',
        default_amount: '',
        default_percentage: '',
        is_taxable: true,
        description: ''
      },
      modal: null,
      formModal: null
    };
  },
  computed: {
    isFormValid() {
      if (!this.allowanceForm.allowance_name) return false;

      if (this.allowanceForm.amount_type === 'fixed') {
        return true; // Default amount is optional
      } else if (this.allowanceForm.amount_type === 'percentage') {
        return true; // Default percentage is optional
      }

      return true;
    }
  },
  mounted() {
    this.initModals();
  },
  methods: {
    initModals() {
      this.$nextTick(() => {
        // Initialize the main modal
        this.modal = new bootstrap.Modal(document.getElementById('allowanceTypesManagementModal'));

        // Initialize form modal
        this.formModal = new bootstrap.Modal(document.getElementById('allowanceTypeFormModal'));

        // Listen for modal hidden events to reset form
        document.getElementById('allowanceTypeFormModal').addEventListener('hidden.bs.modal', this.resetForm);
      });
    },
    showModal() {
      if (this.modal) {
        this.loadAllowanceTypes();
        this.modal.show();
      } else {
        this.initModals();
        setTimeout(() => {
          if (this.modal) {
            this.loadAllowanceTypes();
            this.modal.show();
          }
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    showAddModal() {
      this.isEditing = false;
      this.currentId = null;
      this.resetForm();
      if (this.formModal) {
        this.formModal.show();
      }
    },
    showEditModal() {
      if (this.formModal) {
        this.formModal.show();
      }
    },
    hideFormModal() {
      if (this.formModal) {
        this.formModal.hide();
      }
    },
    resetForm() {
      this.allowanceForm = {
        allowance_name: '',
        amount_type: 'fixed',
        default_amount: '',
        default_percentage: '',
        is_taxable: true,
        description: ''
      };
      this.submitLoading = false;
    },
    loadAllowanceTypes() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAllowanceTypes");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.allowanceTypes = res.data.data.allowanceTypes || [];
            } else {
              console.error("Error fetching allowance types:", res.data.message);
              alert(res.data.message || "Failed to load allowance types");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading allowance types");
          });
    },
    editAllowanceType(allowance) {
      this.isEditing = true;
      this.currentId = allowance.id;

      // Copy allowance data to form
      this.allowanceForm = {
        allowance_name: allowance.allowance_name,
        amount_type: allowance.is_percentage ? 'percentage' : 'fixed',
        default_amount: allowance.default_amount || '',
        default_percentage: allowance.default_percentage || '',
        is_taxable: allowance.is_taxable,
        description: allowance.description || ''
      };

      this.showEditModal();
    },
    deleteAllowanceType(id) {
      if (!confirm("Are you sure you want to delete this allowance type? This may affect employee allowances.")) {
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "deleteAllowanceType");
      data.append("id", id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              alert(res.data.message || "Allowance type deleted successfully");
              this.loadAllowanceTypes();
            } else {
              alert(res.data.message || "Failed to delete allowance type");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while deleting allowance type");
          });
    },
    saveAllowanceType() {
      if (!this.isFormValid) {
        alert("Please fill all required fields");
        return;
      }

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", this.isEditing ? "updateAllowanceType" : "createAllowanceType");

      if (this.isEditing) {
        data.append("id", this.currentId);
      }

      data.append("allowance_name", this.allowanceForm.allowance_name);
      data.append("is_percentage", this.allowanceForm.amount_type === 'percentage' ? 1 : 0);
      data.append("is_taxable", this.allowanceForm.is_taxable ? 1 : 0);

      if (this.allowanceForm.amount_type === 'fixed' && this.allowanceForm.default_amount) {
        data.append("default_amount", this.allowanceForm.default_amount);
      }

      if (this.allowanceForm.amount_type === 'percentage' && this.allowanceForm.default_percentage) {
        data.append("default_percentage", this.allowanceForm.default_percentage);
      }

      if (this.allowanceForm.description) {
        data.append("description", this.allowanceForm.description);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || (this.isEditing ? "Allowance type updated successfully" : "Allowance type created successfully"));
              this.hideFormModal();
              this.loadAllowanceTypes();
            } else {
              alert(res.data.message || "Failed to save allowance type");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while saving allowance type");
          });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    }
  }
};
</script>

<style scoped>
.form-check {
  margin-bottom: 0.5rem;
}

.alert {
  margin-top: 1rem;
}
</style>